import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Checkbox, Icon, Button, Row, Col } from 'antd';
import { getOrderTypeIcon } from '../orders/OrderTypeIcons';
import MixedGallery from '../../stories/MixedGallery';
import BaseModal from '../BaseModal';
import { printFile } from '../../common/preview';
import AppConfig from '../../config/AppConfig';
import { getFileType, getFileIcon } from '../../common/Common';
import { momentTime } from '../../helpers/date_functions';
import _ from 'lodash';
import moment_tz from 'moment-timezone';
import './InvoiceOrderAttachments.css';

const InvoiceOrderAttachments = forwardRef(({ emailAttachments = [], onSelectionChange, warehouseTimeZoneId, warehouseTzShortName }, ref) => {
  const [expandedOrders, setExpandedOrders] = useState({});
  const [selectedOrders, setSelectedOrders] = useState({});
  const [selectedAttachments, setSelectedAttachments] = useState({});
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [currentPreviewImage, setCurrentPreviewImage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredChevronId, setHoveredChevronId] = useState(null);
  const [hoveredAttachmentKey, setHoveredAttachmentKey] = useState(null);
  const [invalidFiles, setInvalidFiles] = useState({});

  const isFileViewable = (fileUrl) => {
    if (!fileUrl) return false;
    
    const urlWithoutQuery = fileUrl.split('?')[0];
    const extension = urlWithoutQuery.split('.').pop().toLowerCase();
    
    const fileTypes = AppConfig.fileTypes;
    for (const type in fileTypes) {
      if (fileTypes[type].includes(extension)) {
        return true;
      }
    }
    
    return false;
  };

  const transformedOrders = emailAttachments.map(order => {
    const allAttachments = [];
    
    if (Array.isArray(order.attachments)) {
      order.attachments.forEach(att => {
        if (att.file_url !== null && att.file_id) {
          allAttachments.push(att);
        }
      });
    }
    
    return {
      ...order,
      attachments: allAttachments
    };
  });


  const getEmailAttachmentsPayload = (attachmentsState = null) => {
    const payload = {};
    const selectedState = attachmentsState || selectedAttachments;
    
    Object.keys(selectedState).forEach(key => {
      if (!selectedState[key]) return;
      
      const parts = key.split('_');
      if (parts.length < 2) return;
      
      const orderId = parts[0];
      const fileId = parts.slice(1).join('_'); 
      
      let attachment = null;
      for (const order of transformedOrders) {
        if (order.order_id === orderId) {
          attachment = order.attachments.find(att => att.file_id === fileId);
          if (attachment) break;
        }
      }
      
      if (!attachment || !attachment.file_id) return;
      
      if (!payload[orderId]) {
        payload[orderId] = [];
      }
      
      if (!payload[orderId].includes(attachment.file_id)) {
        payload[orderId].push(attachment.file_id);
      }
    });
    
    return payload;
  };

  useImperativeHandle(ref, () => ({
    getEmailAttachmentsPayload,
    setSearchTerm,
    getCategoryCounts,
    handleCategorySelection,
    getTotalSelectedCount
  }));

  useEffect(() => {
    const newInvalidFiles = {};
    
    transformedOrders.forEach(order => {
      order.attachments.forEach(att => {
        if (att.file_id && att.file_url) {
          const key = `${order.order_id}_${att.file_id}`;
          if (!isFileViewable(att.file_url)) {
            newInvalidFiles[key] = true;
          }
        }
      });
    });
    
    setInvalidFiles(newInvalidFiles);
  }, [emailAttachments]);

  useEffect(() => {
    if (emailAttachments.length > 0 && onSelectionChange) {
      const counts = getCategoryCounts();
      const summary = getTotalSelectedCount();
      onSelectionChange({
        counts,
        summary,
        payload: getEmailAttachmentsPayload()
      });
    }
  }, [emailAttachments, invalidFiles]);

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders(prev => {
      const isCurrentlyExpanded = prev[orderId];
      if (!isCurrentlyExpanded) {
        return { [orderId]: true };
      } else {
        const newState = { ...prev };
        delete newState[orderId];
        return newState;
      }
    });
  };

  const handleOrderCheckboxChange = (orderId, checked) => {
    const order = transformedOrders.find(o => o.order_id === orderId);
    if (!order || order.attachments.length === 0) return;

    const newSelectedAttachments = { ...selectedAttachments };
    const newSelectedOrders = { ...selectedOrders };

    if (checked) {
      newSelectedOrders[orderId] = true;
      order.attachments.forEach(att => {
        if (att.file_id) {
          const key = `${orderId}_${att.file_id}`;
          if (!invalidFiles[key]) {
            newSelectedAttachments[key] = true;
          }
        }
      });
    } else {
      delete newSelectedOrders[orderId];
      Object.keys(newSelectedAttachments).forEach(key => {
        if (key.startsWith(`${orderId}_`)) {
          delete newSelectedAttachments[key];
        }
      });
    }
    
    setSelectedAttachments(newSelectedAttachments);
    setSelectedOrders(newSelectedOrders);
    
    if (onSelectionChange) {
      const counts = getCategoryCounts(newSelectedAttachments);
      const summary = getTotalSelectedCount(newSelectedAttachments);
      onSelectionChange({
        orderId,
        totalAttachments: order.attachments.length,
        selectedCount: checked ? order.attachments.filter(att => att.file_id).length : 0,
        counts,
        summary,
        payload: getEmailAttachmentsPayload(newSelectedAttachments)
      });
    }
  };

  const handleAttachmentCheckboxChange = (orderId, fileId, checked) => {
    if (!fileId) return;
    const key = `${orderId}_${fileId}`;
    if (invalidFiles[key]) return; 
    
    const newSelectedAttachments = { ...selectedAttachments };
    
    if (checked) {
      newSelectedAttachments[key] = true;
    } else {
      delete newSelectedAttachments[key];
    }

    const order = transformedOrders.find(o => o.order_id === orderId);
    const newSelectedOrders = { ...selectedOrders };
    
    if (order) {
      const attachmentsWithFileId = order.attachments.filter(att => att.file_id);
      const allSelected = attachmentsWithFileId.length > 0 && 
        attachmentsWithFileId.every(att => 
          newSelectedAttachments[`${orderId}_${att.file_id}`]
        );
      
      if (allSelected) {
        newSelectedOrders[orderId] = true;
      } else {
        delete newSelectedOrders[orderId];
      }
    }
    
    setSelectedAttachments(newSelectedAttachments);
    setSelectedOrders(newSelectedOrders);

    if (onSelectionChange && order) {
      const selectedCount = order.attachments.filter(att => 
        att.file_id && newSelectedAttachments[`${orderId}_${att.file_id}`]
      ).length;
        const counts = getCategoryCounts(newSelectedAttachments);
      const summary = getTotalSelectedCount(newSelectedAttachments);
        onSelectionChange({
          orderId,
          totalAttachments: order.attachments.length,
          selectedCount,
          counts,
          summary,
          payload: getEmailAttachmentsPayload(newSelectedAttachments)
        });
    }
  };


  const getSelectedCount = (orderId) => {
    const order = transformedOrders.find(o => o.order_id === orderId);
    if (!order) return 0;
    return order.attachments.filter(att => 
      att.file_id && selectedAttachments[`${orderId}_${att.file_id}`]
    ).length;
  };

  const getAllAttachments = () => {
    const allAtts = [];
    transformedOrders.forEach(order => {
      order.attachments.forEach(att => {
        allAtts.push({
          ...att,
          order_id: order.order_id
        });
      });
    });
    return allAtts;
  };

  const isPOD = (attachment) => {
    if (!attachment.file_code) return false;
    return attachment.file_code.toUpperCase().includes('POD');
  };

  const isBOL = (attachment) => {
    if (!attachment.file_code) return false;
    const fileCodeUpper = attachment.file_code.toUpperCase();
    return fileCodeUpper.includes('BOL') || fileCodeUpper.includes('MAWB');
  };

  const isSignature = (attachment) => {
    return attachment.file_type === 'signature';
  };

  const isDriver = (attachment) => {
    return !isPOD(attachment) && !isBOL(attachment) && !isSignature(attachment);
  };

  const getCategoryCounts = (attachmentsState = null) => {
    const allAtts = getAllAttachments();
    const selectedState = attachmentsState || selectedAttachments;
    const counts = {
      pods: { total: 0, selected: 0 },
      bols: { total: 0, selected: 0 },
      signatures: { total: 0, selected: 0 },
      drivers: { total: 0, selected: 0 }
    };

    allAtts.forEach(att => {
      if (!att.file_id) return;
      const key = `${att.order_id}_${att.file_id}`;
      if (invalidFiles[key]) return;
      const isSelected = selectedState[key] || false;

      if (isPOD(att)) {
        counts.pods.total++;
        if (isSelected) counts.pods.selected++;
      } else if (isBOL(att)) {
        counts.bols.total++;
        if (isSelected) counts.bols.selected++;
      } else if (isSignature(att)) {
        counts.signatures.total++;
        if (isSelected) counts.signatures.selected++;
      } else if (isDriver(att)) {
        counts.drivers.total++;
        if (isSelected) counts.drivers.selected++;
      }
    });

    return counts;
  };

  const handleCategorySelection = (category, checked) => {
    const allAtts = getAllAttachments();
    const newSelectedAttachments = { ...selectedAttachments };
    const newSelectedOrders = { ...selectedOrders };

    let matchingAttachments = [];
    if (category === 'pods') {
      matchingAttachments = allAtts.filter(att => isPOD(att));
    } else if (category === 'bols') {
      matchingAttachments = allAtts.filter(att => isBOL(att));
    } else if (category === 'signatures') {
      matchingAttachments = allAtts.filter(att => isSignature(att));
    } else if (category === 'drivers') {
      matchingAttachments = allAtts.filter(att => isDriver(att));
    }

    matchingAttachments.forEach(att => {
      if (!att.file_id) return; 
      const key = `${att.order_id}_${att.file_id}`;
      if (invalidFiles[key]) return; 
      if (checked) {
        newSelectedAttachments[key] = true;
      } else {
        delete newSelectedAttachments[key];
      }
    });

    setSelectedAttachments(newSelectedAttachments);

    transformedOrders.forEach(order => {
      const attachmentsWithFileId = order.attachments.filter(att => att.file_id);
      const allSelected = attachmentsWithFileId.length > 0 && 
        attachmentsWithFileId.every(att => 
          newSelectedAttachments[`${order.order_id}_${att.file_id}`]
        );
      if (allSelected) {
        newSelectedOrders[order.order_id] = true;
      } else {
        delete newSelectedOrders[order.order_id];
      }
    });

    setSelectedOrders(newSelectedOrders);

    if (onSelectionChange) {
      const counts = getCategoryCounts(newSelectedAttachments);
      const summary = getTotalSelectedCount(newSelectedAttachments);
      onSelectionChange({
        category,
        counts,
        summary,
        payload: getEmailAttachmentsPayload(newSelectedAttachments)
      });
    }
  };

  const handlePreview = (orderId, attachmentUrl, allAttachments) => {
    const files = allAttachments
      .filter(att => att.file_url !== null)
      .map(att => {
        const extension = getFileExtension(att.file_url);
        const fileType = getFileTypeFromUrl(att.file_url);
        const fileName = att.file_name || att.file_code || 'Unnamed Document';
        const fileExtension = extension ? `.${extension}` : '';
        const fileNameWithExtension = fileName.includes('.') ? fileName : `${fileName}${fileExtension}`;
        return {
          fileUrl: att.file_url,
          originalTitle: fileNameWithExtension,
          type: fileType
        };
      });

    const clickedFile = files.find(f => f.fileUrl === attachmentUrl);
    const initialImage = clickedFile ? clickedFile.fileUrl : (files[0]?.fileUrl || null);

    setPreviewFiles(files);
    setCurrentPreviewImage(initialImage);
    setPreviewVisible(true);
  };

  const getFileExtension = (url) => {
    if (!url) return '';
    const urlWithoutQuery = url.split('?')[0];
    const extension = urlWithoutQuery.split('.').pop().toLowerCase();
    return extension;
  };

  const getFileTypeFromUrl = (url) => {
    if (!url) return 'file';
    const extension = getFileExtension(url);
    return getFileType(url);
  };

  const getFileIconType = (fileUrl, fileType = null) => {
    if (fileType === 'signature') {
      return 'edit';
    }
    const detectedType = getFileTypeFromUrl(fileUrl);
    return getFileIcon(detectedType);
  };


  const filteredOrders = transformedOrders.filter(order => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      order.order_number?.toLowerCase().includes(searchLower) ||
      order.order_id?.toLowerCase().includes(searchLower)
    );
  });

  const getTotalSelectedCount = (attachmentsState = null) => {
    let totalSelected = 0;
    let totalOrders = 0;
    let totalAttachments = 0;
    const selectedState = attachmentsState || selectedAttachments;
    
    transformedOrders.forEach(order => {
      const orderAttachments = order.attachments.filter(att => att.file_id && !invalidFiles[`${order.order_id}_${att.file_id}`]);
      if (orderAttachments.length > 0) {
        totalOrders++;
        totalAttachments += orderAttachments.length;
        orderAttachments.forEach(att => {
          const key = `${order.order_id}_${att.file_id}`;
          if (selectedState[key]) {
            totalSelected++;
          }
        });
      }
    });
    
    return { totalSelected, totalOrders, totalAttachments };
  };

  const renderOrderRow = (order, index, totalFilteredCount) => {
    const isExpanded = expandedOrders[order.order_id];
    const selectedCount = getSelectedCount(order.order_id);
    const totalAttachments = order.attachments.length;
    const attachmentsWithFileId = order.attachments.filter(att => att.file_id).length;
    const isOrderSelected = selectedOrders[order.order_id];
    const isChevronHovered = hoveredChevronId === order.order_id;
    const isIndeterminate = selectedCount > 0 && selectedCount < attachmentsWithFileId;

    const groupedByCode = {};
    order.attachments.forEach(att => {
      const code = att.file_code || 'Other';
      if (!groupedByCode[code]) {
        groupedByCode[code] = [];
      }
      groupedByCode[code].push(att);
    });

    return (
      <div
        key={order.order_id}
        style={{
          borderBottom: index < totalFilteredCount - 1 ? '1px solid #e8e8e8' : 'none',
          padding: '12px',
        }}
      >
        <Row type="flex" align="middle" gutter={16} style={{ width: '100%' }}>
          <Col>
            <Checkbox
              checked={isOrderSelected}
              indeterminate={isIndeterminate}
              disabled={attachmentsWithFileId === 0}
              onChange={(e) => handleOrderCheckboxChange(order.order_id, e.target.checked)}
            />
          </Col>
          <Col>
            {getOrderTypeIcon(order.order_type, { height: "24", width: "24" })}
          </Col>
          <Col flex="1">
            <span style={{ fontWeight: 600 }}>#{order.order_number}</span>
          </Col>
          <Col style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button
              type="default"
              disabled={totalAttachments === 0}
              onClick={() => toggleOrderExpansion(order.order_id)}
              style={{
                borderRadius: '16px',
                border: '1px solid #d9d9d9',
              }}
            >
              {selectedCount}/{attachmentsWithFileId} selected
            </Button>
            <Icon
              type={isExpanded ? 'up' : 'down'}
              onClick={() => toggleOrderExpansion(order.order_id)}
              onMouseEnter={() => setHoveredChevronId(order.order_id)}
              onMouseLeave={() => setHoveredChevronId(null)}
              style={{
                cursor: 'pointer',
                fontSize: '14px',
                color: isChevronHovered ? '#1890ff' : '#595959',
                transition: 'color 0.2s ease',
                padding: '4px',
                borderRadius: '4px',
                backgroundColor: isChevronHovered ? '#f0f0f0' : 'transparent',
              }}
            />
          </Col>
        </Row>

        {isExpanded && (
          <div 
            style={{ 
              marginTop: '12px', 
              border: '1px solid #e8e8e8',
              borderRadius: '8px',
              padding: '0',
              backgroundColor: '#fafafa',
              overflow: 'hidden',
            }}
          >
            {order.attachments.length === 0 ? (
              <div style={{ padding: '12px', color: '#8c8c8c', fontStyle: 'italic' }}>
                No valid attachments available
              </div>
            ) : (
              Object.keys(groupedByCode).map((code, codeIndex) => {
                const codeAttachments = groupedByCode[code];
                const isLastGroup = codeIndex === Object.keys(groupedByCode).length - 1;
                
                return (
                  <div key={`${order.order_id}_${code}_${codeIndex}`}>
                    {codeAttachments.map((attachment, attIndex) => {
                      const attachmentKey = attachment.file_id ? `${order.order_id}_${attachment.file_id}` : null;
                      const isSelected = attachmentKey ? selectedAttachments[attachmentKey] : false;
                      const fileName = attachment.file_name || 'Unnamed Document';
                      const fileExtension = getFileExtension(attachment.file_url);
                      const fileNameWithExtension = fileExtension ? `${fileName}.${fileExtension}` : fileName;
                      const isAttachmentHovered = hoveredAttachmentKey === attachmentKey;
                      const isLastInGroup = attIndex === codeAttachments.length - 1;
                      const isLastAttachment = isLastGroup && isLastInGroup;

                      return (
                        <Row
                          key={`${attachment.file_id || attachment.file_url}_${attIndex}`}
                          type="flex"
                          align="middle"
                          gutter={16}
                          style={{
                            padding: '4px 12px',
                            margin: 0,
                            borderBottom: !isLastAttachment ? '1px solid #f0f0f0' : 'none',
                            width: '100%',
                            backgroundColor: isAttachmentHovered ? '#f5f5f5' : 'transparent',
                            transition: 'background-color 0.2s ease',
                          }}
                          onMouseEnter={() => setHoveredAttachmentKey(attachmentKey)}
                          onMouseLeave={() => setHoveredAttachmentKey(null)}
                        >
                          <Col>
                            <Checkbox
                              checked={isSelected}
                              disabled={!attachment.file_id || invalidFiles[attachmentKey]}
                              onChange={(e) => {
                                if (attachment.file_id && !invalidFiles[attachmentKey]) {
                                  handleAttachmentCheckboxChange(order.order_id, attachment.file_id, e.target.checked);
                                }
                              }}
                            />
                          </Col>
                          <Col>
                            <Icon 
                              type={getFileIconType(attachment.file_url, attachment.file_type)} 
                              style={{ fontSize: '18px' }} 
                            />
                          </Col>
                          <Col flex="1">
                            <div>
                              <div style={{ fontWeight: 500, color: invalidFiles[attachmentKey] ? '#ff4d4f' : 'inherit' }}>
                                {fileName}
                                {invalidFiles[attachmentKey] && (
                                  <span style={{ marginLeft: '8px', fontSize: '12px', color: '#ff4d4f' }}>
                                    (Not Viewable)
                                  </span>
                                )}
                              </div>
                              {attachment.file_code && (
                                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                  {attachment.file_code}
                                </div>
                              )}
                            </div>
                          </Col>
                          <Col style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                minWidth: "320px", 
                              }}
                            >
                            <span style={{ minWidth: 140 }}>

                                                          {attachment.captured_at && warehouseTimeZoneId && (
                                <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                  {momentTime(AppConfig.dateTimeFormat, attachment.captured_at, warehouseTimeZoneId)}
                                  {warehouseTzShortName && ` ${warehouseTzShortName}`}
                                </span>
                              )}
                              </span>
                            <Button
                              type="link"
                              icon="eye"
                              onClick={() =>
                                handlePreview(order.order_id, attachment.file_url, order.attachments)
                              }
                              style={{ padding: 0, maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                              {fileNameWithExtension || 'Unnamed Document'}
                            </Button>


         </div>
                          </Col>

                        </Row>
                      );
                    })}
                    {!isLastGroup && (
                      <div style={{ height: '8px' }} /> 
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        className="invoice-order-attachments"
        style={{
          border: '1px solid #e8e8e8',
          borderRadius: '8px',
          maxHeight: '400px',
          overflowY: 'auto',
          marginTop: '16px',
        }}
      >
        {transformedOrders.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#8c8c8c' }}>
            No attachments available
          </div>
        ) : filteredOrders.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#8c8c8c' }}>
            No orders found matching "{searchTerm}"
          </div>
        ) : (
          filteredOrders.map((order, index) => renderOrderRow(order, index, filteredOrders.length))
        )}
      </div>

      {previewVisible && (
        <BaseModal
          title="Preview Attachments"
          visible={previewVisible}
          onCancel={() => {
            setPreviewVisible(false);
            setCurrentPreviewImage(null);
          }}
          width="80%"
          style={{ top: 30 }}
          footer={null}
          destroyOnClose
        >
          <MixedGallery
            files={previewFiles}
            currentImage={currentPreviewImage}
            showPrintButton={true}
            showFileName={true}
            closeComponent={() => {
              setPreviewVisible(false);
              setCurrentPreviewImage(null);
            }}
            handlePrint={printFile}
          />
        </BaseModal>
      )}
    </>
  );
});

export default InvoiceOrderAttachments;
